import { cn } from '@repo/lib/cn';

import { useProgressiveImageLoader } from '@/hooks/image-loader/useProgressiveImageLoader';

import type { ProgressiveImageProps } from '.';

export function ProgressiveImage(props: ProgressiveImageProps) {
  const { className, pictureClassName, sources, alt, width, height, onLoad, onError, thumbnail, type, showLoadingState = true, showErrorState = true, loadingComponent, errorComponent } = props;

  const { isLoaded, isError, base64, pictureRef } = useProgressiveImageLoader({
    thumbnail,
    type,
    onLoad,
    onError,
  });

  const wrapperClasses = cn('text-muted-foreground relative overflow-hidden', className);

  const skeletonClasses = cn(
    'bg-muted/50 absolute inset-0 flex animate-pulse items-center justify-center rounded-xl duration-500',
    !isLoaded && !isError ? 'opacity-100' : 'animate-fade-out opacity-0'
  );

  const errorSkeletonClasses = cn('bg-muted/50 absolute inset-0 flex items-center justify-center rounded-xl duration-200', isError ? 'opacity-100' : 'animate-fade-out opacity-0');

  const base64Classes = cn('absolute inset-0 object-cover blur-sm transition-opacity duration-200', !isLoaded && !!base64 && !isError ? 'opacity-100' : 'animate-fade-out opacity-0');

  const imageClasses = cn('h-full w-full object-cover transition-opacity duration-200', isLoaded && !isError ? 'animate-fade-in opacity-100' : 'opacity-0');

  return (
    <div className={wrapperClasses} style={{ width, height }}>
      {/* Loading skeleton */}
      {showLoadingState && !isLoaded && !isError && !base64 && (loadingComponent ? <div className={skeletonClasses}>{loadingComponent}</div> : <div className={skeletonClasses} />)}
      {/* Base64 blurred thumbnail */}
      {base64 && !isLoaded && !isError && <img src={base64} className={base64Classes} width={width} height={height} aria-hidden="true" />}
      {/* Error skeleton */}
      {showErrorState && isError && (errorComponent ? <div className={errorSkeletonClasses}>{errorComponent}</div> : <div className={errorSkeletonClasses} />)}
      {/* Progressive image */}
      <picture ref={pictureRef} className={cn('absolute inset-0 z-0', pictureClassName)} style={{ width: '100%', height: '100%' }}>
        {/* Desktop WebP */}
        <source media="(min-width: 1024px)" srcSet={sources.desktop.lowRes.webp} data-srcset={sources.desktop.highRes.webp} type="image/webp" />
        {/* Desktop JPEG fallback */}
        <source media="(min-width: 1024px)" srcSet={sources.desktop.lowRes.jpeg} data-srcset={sources.desktop.highRes.jpeg} type="image/jpeg" />

        {/* Tablet WebP */}
        <source media="(min-width: 768px)" srcSet={sources.tablet.lowRes.webp} data-srcset={sources.tablet.highRes.webp} type="image/webp" />
        {/* Tablet JPEG fallback */}
        <source media="(min-width: 768px)" srcSet={sources.tablet.lowRes.jpeg} data-srcset={sources.tablet.highRes.jpeg} type="image/jpeg" />

        {/* Mobile WebP */}
        <source media="(max-width: 767px)" srcSet={sources.mobile.lowRes.webp} data-srcset={sources.mobile.highRes.webp} type="image/webp" />
        {/* Mobile JPEG fallback */}
        <source media="(max-width: 767px)" srcSet={sources.mobile.lowRes.jpeg} data-srcset={sources.mobile.highRes.jpeg} type="image/jpeg" />

        {/* Fallback Image */}
        <img
          className={imageClasses}
          src={sources.mobile.lowRes.jpeg}
          data-src={sources.mobile.highRes.jpeg}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          aria-hidden={isError}
        />
      </picture>
    </div>
  );
}
